import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Typography,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import {
  FilterList,
  ExpandMore,
  ExpandLess,
  Clear,
} from '@mui/icons-material';
import { SearchFilters, Specialty } from '../services/caretakerSearchService';

interface CaretakerFiltersProps {
  filters: SearchFilters;
  specialties: Specialty[];
  onFiltersChange: (filters: SearchFilters) => void;
  loading?: boolean;
}

const AVAILABILITY_OPTIONS = [
  { value: 'now', label: 'Available Now' },
  { value: 'this_week', label: 'Available This Week' },
  { value: 'this_month', label: 'Available This Month' },
];

const RATING_OPTIONS = [
  { value: 4, label: '4+ Stars' },
  { value: 3, label: '3+ Stars' },
  { value: 2, label: '2+ Stars' },
  { value: 1, label: '1+ Stars' },
];

const CaretakerFilters: React.FC<CaretakerFiltersProps> = ({
  filters,
  specialties,
  onFiltersChange,
  loading = false
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleSpecialtyChange = (specialtyName: string) => {
    onFiltersChange({
      ...filters,
      specialty: specialtyName === 'all' ? '' : specialtyName,
    });
  };

  const handleRatingChange = (minRating: number | '') => {
    onFiltersChange({
      ...filters,
      minRating: minRating === '' ? undefined : minRating,
    });
  };

  const handleAvailabilityChange = (availability: string) => {
    onFiltersChange({
      ...filters,
      availability: availability === 'all' ? undefined : availability as any,
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      query: '',
      specialty: '',
      availability: undefined,
      minRating: undefined,
      services: [],
    });
  };

  const hasActiveFilters = () => {
    return !!(
      filters.specialty ||
      filters.availability ||
      filters.minRating ||
      (filters.services && filters.services.length > 0)
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.specialty) count++;
    if (filters.availability) count++;
    if (filters.minRating) count++;
    if (filters.services && filters.services.length > 0) count++;
    return count;
  };

  return (
    <Box>
      {/* Filter Toggle Button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            color="primary"
            size="small"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Typography variant="h6" component="h2">
            Filters
          </Typography>
          {hasActiveFilters() && (
            <Chip
              label={getActiveFiltersCount()}
              size="small"
              color="primary"
              sx={{ minWidth: 24, height: 24 }}
            />
          )}
        </Box>
        
        {hasActiveFilters() && (
          <Button
            startIcon={<Clear />}
            onClick={clearFilters}
            size="small"
            color="inherit"
            disabled={loading}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Filters Content */}
      <Collapse in={expanded}>
        <Box>
          <Grid container spacing={2}>
            {/* Specialty Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Specialty</InputLabel>
                <Select
                  value={filters.specialty || 'all'}
                  onChange={(e) => handleSpecialtyChange(e.target.value)}
                  label="Specialty"
                  disabled={loading}
                >
                  <MenuItem value="all">All Specialties</MenuItem>
                  {specialties.map((specialty) => (
                    <MenuItem key={specialty.id} value={specialty.name}>
                      {specialty.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Availability Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Availability</InputLabel>
                <Select
                  value={filters.availability || 'all'}
                  onChange={(e) => handleAvailabilityChange(e.target.value)}
                  label="Availability"
                  disabled={loading}
                >
                  <MenuItem value="all">Any Time</MenuItem>
                  {AVAILABILITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Rating Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Minimum Rating</InputLabel>
                <Select
                  value={filters.minRating || ''}
                  onChange={(e) => handleRatingChange(e.target.value as number | '')}
                  label="Minimum Rating"
                  disabled={loading}
                >
                  <MenuItem value="">Any Rating</MenuItem>
                  {RATING_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Services Filter - Multiple Selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Services</InputLabel>
                <Select
                  multiple
                  value={filters.services || []}
                  onChange={(e) => handleFilterChange('services', e.target.value)}
                  label="Services"
                  disabled={loading}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {specialties.map((specialty) => (
                    <MenuItem key={specialty.id} value={specialty.name}>
                      {specialty.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Active Filters Display */}
          {hasActiveFilters() && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Active filters:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {filters.specialty && (
                  <Chip
                    label={`Specialty: ${filters.specialty}`}
                    onDelete={() => handleSpecialtyChange('all')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filters.availability && (
                  <Chip
                    label={`Availability: ${AVAILABILITY_OPTIONS.find(opt => opt.value === filters.availability)?.label}`}
                    onDelete={() => handleAvailabilityChange('all')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filters.minRating && (
                  <Chip
                    label={`Rating: ${filters.minRating}+ Stars`}
                    onDelete={() => handleRatingChange('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filters.services && filters.services.length > 0 && (
                  filters.services.map((service) => (
                    <Chip
                      key={service}
                      label={`Service: ${service}`}
                      onDelete={() => {
                        const newServices = filters.services?.filter(s => s !== service) || [];
                        handleFilterChange('services', newServices);
                      }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CaretakerFilters;
